import React from 'react';
import { Building2, TrendingUp, Users, PieChart, Globe, Truck, Eye, Banknote, Cpu, Scale, ArrowRight, Zap } from 'lucide-react';

const ProblemSolution = ({ lang }) => {
  const content = {
    en: {
      // Header
      label: 'THE CHALLENGE',
      title: 'The Structural Paradox',
      subtitle: 'Latin America feeds the world but remains underfunded. Producers bear the risk; intermediaries capture the value.',
      
      // Stats
      statsLabel: 'LATAM SME REALITY',
      stats: [
        { value: '12.9M', label: 'SMEs', icon: Building2, highlight: false },
        { value: '99.5%', label: 'of Ecosystem', icon: PieChart, highlight: false },
        { value: '60%', label: 'of Jobs', icon: Users, highlight: false },
        { value: '25%', label: 'of GDP', icon: TrendingUp, highlight: true, sublabel: 'Only' },
        { value: '10%', label: 'Export', icon: Globe, highlight: true, sublabel: 'Only' }
      ],
      
      // Barriers
      barriersLabel: 'STRUCTURAL BARRIERS',
      barriers: [
        {
          icon: Truck,
          title: 'Logistics Wall',
          description: '52% more bureaucracy & inefficient transport.'
        },
        {
          icon: Eye,
          title: 'Trade Blindness',
          description: 'Lack of data on global buyers & certifications.'
        },
        {
          icon: Banknote,
          title: 'Financial Gap',
          description: '70% of SMEs lack access to trade finance.'
        },
        {
          icon: Cpu,
          title: 'Tech Divide',
          description: 'Low productivity & lack of digital traceability.'
        },
        {
          icon: Scale,
          title: 'Limited Scale',
          description: 'Dependence on local markets & intermediaries.'
        }
      ],
      
      // Solution
      solutionLabel: 'THE SOLUTION',
      solutionHeadline: 'EQTOS: The AI Operating System connecting Latin America directly to the $1.9T Global Market.',
      solutionSubtext: 'Our main objective is for more Latin American producers to sell their "semi-finished and finished" products directly to the international market.',
      solutionCta: 'Apply to Pilot Program'
    },
    
    es: {
      // Header
      label: 'EL DESAFÍO',
      title: 'La Paradoja Estructural',
      subtitle: 'Latinoamérica alimenta al mundo pero no se enriquece. El productor asume el riesgo; los intermediarios capturan el valor.',
      
      // Stats
      statsLabel: 'REALIDAD PYME LATAM',
      stats: [
        { value: '12.9M', label: 'PYMES', icon: Building2, highlight: false },
        { value: '99.5%', label: 'del Ecosistema', icon: PieChart, highlight: false },
        { value: '60%', label: 'del Empleo', icon: Users, highlight: false },
        { value: '25%', label: 'del PIB', icon: TrendingUp, highlight: true, sublabel: 'Solo' },
        { value: '10%', label: 'Exporta', icon: Globe, highlight: true, sublabel: 'Solo' }
      ],
      
      // Barriers
      barriersLabel: 'BARRERAS ESTRUCTURALES',
      barriers: [
        {
          icon: Truck,
          title: 'Muro Logístico',
          description: '52% más burocracia y transporte ineficiente.'
        },
        {
          icon: Eye,
          title: 'Ceguera Comercial',
          description: 'Falta de datos sobre compradores y certificaciones.'
        },
        {
          icon: Banknote,
          title: 'Brecha Financiera',
          description: '70% de PYMES sin acceso a financiamiento.'
        },
        {
          icon: Cpu,
          title: 'Brecha Tecnológica',
          description: 'Baja productividad y falta de trazabilidad.'
        },
        {
          icon: Scale,
          title: 'Escala Limitada',
          description: 'Dependencia de mercados locales e intermediarios.'
        }
      ],
      
      // Solution
      solutionLabel: 'LA SOLUCIÓN',
      solutionHeadline: 'EQTOS: El Sistema Operativo de IA que conecta Latinoamérica directo al Mercado Global de $1.9T.',
      solutionSubtext: 'El objetivo principal es que más productores latinoamericanos vendan directamente sus productos "semi-terminados y terminados" al mercado internacional.',
      solutionCta: 'Aplicar al Programa Piloto'
    }
  };

  const t = content[lang] || content.en;

  return (
    <section className="py-16 lg:py-28 bg-[#0a0a0a] border-t border-b border-[#00FFD1]/10 relative w-full overflow-hidden">
      {/* Safe wrapper for mobile */}
      <div className="w-full overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 50%, #00FFD1 1px, transparent 1px),
                radial-gradient(circle at 80% 50%, #00FFD1 1px, transparent 1px)
              `,
              backgroundSize: '100px 100px'
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 relative z-10">
          
          {/* Header */}
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 border border-red-500/40 bg-red-500/5 mb-4 sm:mb-6">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-red-400 text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em]">{t.label}</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
            {t.title}
          </h2>
          
          <p className="text-base sm:text-lg md:text-xl text-white/60 max-w-3xl mx-auto leading-relaxed px-2">
            {t.subtitle}
          </p>
        </div>

        {/* Stats Grid - Mobile: 2 cols first row, 3 cols second row | Desktop: 5 cols */}
        <div className="mb-12 lg:mb-16">
          <div className="text-center mb-6 sm:mb-8">
            <span className="text-white/40 text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em]">{t.statsLabel}</span>
          </div>
          
          {/* First 3 stats */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
            {t.stats.map((stat, idx) => (
              <div 
                key={idx}
                className={`
                  relative p-3 sm:p-6 text-center border transition-all duration-300
                  ${stat.highlight 
                    ? 'bg-red-500/10 border-red-500/40 hover:border-red-500' 
                    : 'bg-black/50 border-white/10 hover:border-white/30'
                  }
                  ${idx >= 3 ? 'col-span-1' : ''}
                `}
              >
                {/* Highlight Badge */}
                {stat.sublabel && (
                  <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2">
                    <span className={`
                      px-1.5 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-bold tracking-wider uppercase whitespace-nowrap
                      ${stat.highlight ? 'bg-red-500 text-white' : 'bg-white/20 text-white/60'}
                    `}>
                      {stat.sublabel}
                    </span>
                  </div>
                )}
                
                <stat.icon className={`
                  w-4 h-4 sm:w-6 sm:h-6 mx-auto mb-2 sm:mb-3
                  ${stat.highlight ? 'text-red-400' : 'text-white/40'}
                `} />
                
                <div className={`
                  text-xl sm:text-3xl md:text-4xl font-bold mb-0.5 sm:mb-1
                  ${stat.highlight ? 'text-red-400' : 'text-white'}
                `}>
                  {stat.value}
                </div>
                
                <div className={`
                  text-[10px] sm:text-sm
                  ${stat.highlight ? 'text-red-400/80' : 'text-white/50'}
                `}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Barriers Grid */}
        <div className="mb-12 lg:mb-16">
          <div className="text-center mb-6 sm:mb-8">
            <span className="text-white/40 text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em]">{t.barriersLabel}</span>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
            {t.barriers.map((barrier, idx) => (
              <div 
                key={idx}
                className={`
                  p-3 sm:p-5 bg-black/50 border border-white/10 hover:border-red-500/30 transition-all duration-300 group
                  ${idx === 4 ? 'col-span-2 sm:col-span-1' : ''}
                `}
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-red-500/10 border border-red-500/30 mb-3 sm:mb-4 group-hover:bg-red-500/20 transition-colors">
                  <barrier.icon className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                </div>
                
                <h4 className="text-white font-semibold text-xs sm:text-sm mb-1 sm:mb-2">
                  {barrier.title}
                </h4>
                
                <p className="text-white/50 text-[10px] sm:text-xs leading-relaxed">
                  {barrier.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Solution Footer */}
        <div className="relative">
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#00FFD1]/10 via-[#00FFD1]/5 to-[#00FFD1]/10 blur-xl pointer-events-none" />
          
          <div className="relative bg-black border-2 border-[#00FFD1]/40 p-5 sm:p-8 md:p-12">
            {/* Corner Accents */}
            <div className="absolute top-0 left-0 w-4 h-4 sm:w-6 sm:h-6 border-t-2 border-l-2 border-[#00FFD1]" />
            <div className="absolute top-0 right-0 w-4 h-4 sm:w-6 sm:h-6 border-t-2 border-r-2 border-[#00FFD1]" />
            <div className="absolute bottom-0 left-0 w-4 h-4 sm:w-6 sm:h-6 border-b-2 border-l-2 border-[#00FFD1]" />
            <div className="absolute bottom-0 right-0 w-4 h-4 sm:w-6 sm:h-6 border-b-2 border-r-2 border-[#00FFD1]" />
            
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 border border-[#00FFD1]/40 bg-[#00FFD1]/5 mb-4 sm:mb-6">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-[#00FFD1]" />
                <span className="text-[#00FFD1] text-[10px] sm:text-xs font-semibold tracking-[0.15em] sm:tracking-[0.2em]">{t.solutionLabel}</span>
              </div>
              
              <h3 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 max-w-4xl mx-auto leading-tight px-2">
                {t.solutionHeadline}
              </h3>
              
              <p className="text-white/70 text-sm sm:text-base md:text-lg max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed px-2">
                {t.solutionSubtext}
              </p>
              
              <a
                href="#waitlist"
                className="inline-flex items-center gap-2 sm:gap-3 bg-[#00FFD1] text-black px-5 py-3 sm:px-8 sm:py-4 text-sm sm:text-base font-semibold hover:bg-[#00FFD1]/90 transition-all duration-300 group"
              >
                {t.solutionCta}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
        
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;
